import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  ApiError,
  type ArquivoDossie,
  type DiscDaEntrevista,
  type DossieCandidato,
  type SugestaoCandidato,
  type SugestaoDisc,
} from "../api";
import { fmt } from "../endomarketing-utils";
import type { Row } from "../types";
import { QUESTOES_DISC, leituraDisc } from "../disc-modelo";
import { imprimirFolhaDisc } from "../disc-folha";
import {
  BarrasDisc,
  enunciadoDaQuestao,
  quando,
  resultadoDaResposta,
  textoAlternativa,
  type QuestaoExibida,
} from "./DiscView";
import {
  IconClipboard,
  IconClose,
  IconDownload,
  IconFile,
  IconLink,
  IconRefresh,
  IconUpload,
  IconUser,
} from "../icons";

/**
 * CURRÍCULO DO CANDIDATO — durante a entrevista.
 *
 * A entrevista já sabia de qual candidato da triagem ela veio
 * (`entrevistas.triagem_resposta_id`), mas o currículo ficava na outra aba.
 * Este painel traz o dossiê para dentro da entrevista: o currículo ABERTO na
 * tela (PDF e imagem embutidos), as perguntas estratégicas com o que a pessoa
 * respondeu, a avaliação que o RH deu na triagem e o resultado do DISC — que
 * chega sozinho assim que a pessoa responde o teste (server/disc-entrevista.ts).
 *
 * Ele é usado em dois lugares, com o MESMO componente:
 *   • como painel lateral do Roteiro de Entrevista — para ler enquanto conduz;
 *   • como modal próprio, pelo botão da linha na aba Entrevistas.
 *
 * Entrevista lançada à mão não tem vínculo: em vez de quebrar, o painel mostra
 * os documentos da própria entrevista e oferece os candidatos da triagem com
 * nome parecido para o RH ligar os dois.
 */

/** Imagens que o navegador abre embutido. O resto cai no aviso de baixar. */
const IMAGENS = new Set(["png", "jpg", "jpeg", "gif", "webp"]);

function tamanhoLegivel(bytes: number): string {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Chave estável do arquivo: o nome sozinho colide entre as duas pastas. */
const chaveArquivo = (a: ArquivoDossie): string => `${a.origem}:${a.nome}`;

/** Nota da triagem em texto (vem NUMERIC do Postgres, às vezes como string). */
function notaTexto(nota: number | string | null | undefined): string | null {
  if (nota == null || nota === "") return null;
  const n = Number(nota);
  return Number.isFinite(n) ? n.toFixed(1).replace(".", ",") : null;
}

/* ------------------------------------------------------------------ *
 *  Visualizador                                                       *
 * ------------------------------------------------------------------ */

/**
 * Mostra o arquivo escolhido. PDF vai em <iframe> e imagem em <img>; Word (e o
 * que mais o candidato tiver mandado) não abre no navegador, então em vez de
 * uma tela em branco o painel diz isso e oferece baixar/abrir em outra aba.
 *
 * Declarado FORA do painel de propósito: componente aninhado é remontado a cada
 * render do pai — o PDF recarregaria (e a página voltaria ao topo) a cada tecla
 * digitada no roteiro ao lado.
 */
function VisualizadorArquivo({ arquivo }: { arquivo: ArquivoDossie | null }) {
  if (!arquivo) {
    return (
      <div className="curr-vazio">
        <IconFile size={26} />
        <p>Nenhum currículo anexado ainda.</p>
        <small className="muted">
          Anexe aqui, ou peça ao candidato pelo link da vaga na Triagem de Currículo.
        </small>
      </div>
    );
  }

  if (arquivo.ext === "pdf") {
    return (
      <iframe
        className="curr-visor"
        // A chave força o navegador a recarregar quando troca de arquivo —
        // sem ela, o iframe às vezes segura o PDF anterior em cache.
        key={arquivo.url}
        src={`${arquivo.url}#view=FitH`}
        title={`Currículo — ${arquivo.nome}`}
      />
    );
  }

  if (IMAGENS.has(arquivo.ext)) {
    return (
      <div className="curr-visor curr-visor-img">
        <img src={arquivo.url} alt={`Currículo — ${arquivo.nome}`} />
      </div>
    );
  }

  return (
    <div className="curr-vazio">
      <IconFile size={26} />
      <p>
        <strong>{arquivo.nome}</strong>
      </p>
      <small className="muted">
        O navegador não abre {arquivo.ext ? `.${arquivo.ext}` : "este tipo"} na tela. Baixe para ler.
      </small>
      <a className="btn-secondary btn-sm" href={arquivo.url} target="_blank" rel="noreferrer">
        <IconDownload size={15} /> Abrir / baixar
      </a>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Vincular uma entrevista solta a um candidato da triagem            *
 * ------------------------------------------------------------------ */

function ListaSugestoes({
  sugestoes,
  vinculando,
  onVincular,
}: {
  sugestoes: SugestaoCandidato[];
  vinculando: number | null;
  onVincular: (s: SugestaoCandidato) => void;
}) {
  if (sugestoes.length === 0) {
    return (
      <p className="muted curr-nota">
        Nenhum candidato na Triagem de Currículo ainda. Cadastre a vaga lá e o currículo aparece aqui
        sozinho.
      </p>
    );
  }
  return (
    <ul className="curr-sugestoes">
      {sugestoes.map((s) => (
        <li key={s.resposta_id} className={s.parecido ? "curr-sug parecido" : "curr-sug"}>
          <div>
            <strong>{s.candidato}</strong>
            {s.parecido && <span className="pill pill-ok curr-pill">nome bate</span>}
            <small className="muted">
              {s.vaga}
              {s.vaga_setor ? ` · ${s.vaga_setor}` : ""}
              {s.situacao ? ` · ${s.situacao}` : ""}
              {notaTexto(s.nota) ? ` · nota ${notaTexto(s.nota)}` : ""}
            </small>
            {s.ja_vinculado && (
              <small className="curr-aviso-inline">Já está ligado a outra entrevista.</small>
            )}
          </div>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => onVincular(s)}
            disabled={vinculando != null}
          >
            <IconLink size={14} /> {vinculando === s.resposta_id ? "Ligando…" : "Vincular"}
          </button>
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------ *
 *  DISC da pessoa                                                     *
 * ------------------------------------------------------------------ */

/** Por que o teste está nesta entrevista — o RH confere antes de confiar. */
const POR_QUE_DISC: Record<string, string> = {
  telefone: "o telefone é o mesmo do candidato",
  "e-mail": "o e-mail é o mesmo do candidato",
  nome: "o nome é o mesmo do candidato",
};

/**
 * O resultado do DISC dentro da entrevista — só LEITURA.
 *
 * O parecer continua sendo escrito na aba DISC: aqui é a consulta durante a
 * conversa (perfil, leitura, o que a pessoa marcou) e o laudo para imprimir.
 * Sem teste ligado, mostra os testes que podem ser da pessoa para o RH ligar.
 *
 * Declarado FORA do painel pelo mesmo motivo do visualizador: componente
 * aninhado é remontado a cada render do pai.
 */
function DiscDoCandidato({
  entrevistaId,
  disc,
  onMudou,
}: {
  entrevistaId: number;
  disc: DiscDaEntrevista | null;
  onMudou: (mensagem: string) => Promise<void>;
}) {
  const [verMarcadas, setVerMarcadas] = useState(false);
  const [sugestoes, setSugestoes] = useState<SugestaoDisc[] | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Sem DISC ligado: os testes que podem ser desta pessoa.
  useEffect(() => {
    if (disc) return;
    let vivo = true;
    setSugestoes(null);
    api.candidatoEntrevista
      .discSugestoes(entrevistaId)
      .then((r) => vivo && setSugestoes(r.sugestoes))
      .catch(() => vivo && setSugestoes([]));
    return () => {
      vivo = false;
    };
  }, [entrevistaId, disc]);

  // O questionário que a pessoa respondeu: o da vaga, ou as 20 do papel.
  const questoes: QuestaoExibida[] = useMemo(
    () => (disc && disc.questoes.length > 0 ? disc.questoes : QUESTOES_DISC),
    [disc],
  );
  const resultado = useMemo(() => (disc ? resultadoDaResposta(disc.resposta) : null), [disc]);
  const leitura = useMemo(
    () => (resultado ? leituraDisc(resultado, questoes.length) : null),
    [resultado, questoes],
  );

  async function vincular(s: SugestaoDisc) {
    setOcupado(true);
    setErro(null);
    try {
      const r = await api.candidatoEntrevista.vincularDisc(entrevistaId, s.resposta_id);
      await onMudou(r.mensagem);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não consegui ligar o DISC.");
    } finally {
      setOcupado(false);
    }
  }

  async function soltar() {
    if (
      !confirm(
        "Este DISC não é desta pessoa?\n\nEle sai desta entrevista (continua na aba DISC) e não volta sozinho.",
      )
    )
      return;
    setOcupado(true);
    setErro(null);
    try {
      const r = await api.candidatoEntrevista.desvincularDisc(entrevistaId);
      setVerMarcadas(false);
      await onMudou(r.mensagem);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não consegui tirar o DISC da entrevista.");
    } finally {
      setOcupado(false);
    }
  }

  /** O mesmo laudo da aba DISC, com o parecer que está gravado. */
  function imprimir() {
    if (!disc || !resultado) return;
    const r = disc.resposta;
    const falha = imprimirFolhaDisc({
      candidato: r.candidato,
      // Mesma escolha da aba DISC: o cargo puro no título, a vaga só depois.
      cargo: disc.aplicacao.cargo ?? null,
      vaga: r.vaga_pretendida ?? disc.aplicacao.nome ?? null,
      email: r.email,
      telefone: r.telefone,
      placar: resultado.placar,
      perfil: resultado.perfil,
      perfilNome: resultado.perfilNome,
      parecer: r.parecer,
      observacao: r.observacao,
      respondidoEm: r.respondido_em,
      avaliador: r.parecer_por,
      respostas: [],
      questoes,
      origemQuestoes: disc.aplicacao.questoes_origem ?? null,
    });
    setErro(falha);
  }

  if (!disc || !resultado || !leitura) {
    return (
      <div className="curr-respostas">
        <p className="curr-nota">
          <strong>O DISC desta pessoa ainda não chegou.</strong> Assim que ela responder o teste pelo
          link (ou o RH lançar o papel na aba DISC), o resultado aparece aqui sozinho — o sistema
          reconhece pelo telefone, e-mail ou nome.
        </p>
        {sugestoes === null ? (
          <p className="muted curr-nota">Procurando testes já respondidos…</p>
        ) : (
          sugestoes.length > 0 && (
            <>
              <p className="muted curr-nota">
                Se ela já respondeu com o nome escrito de outro jeito, escolha o teste:
              </p>
              <ul className="curr-sugestoes">
                {sugestoes.map((s) => (
                  <li key={s.resposta_id} className={s.parecido ? "curr-sug parecido" : "curr-sug"}>
                    <div>
                      <strong>{s.candidato}</strong>
                      {s.como && <span className="pill pill-ok curr-pill">{s.como} bate</span>}
                      <small className="muted">
                        {s.aplicacao}
                        {s.perfil ? ` · perfil ${s.perfil}` : ""} · {quando(s.respondido_em)}
                        {s.telefone ? ` · ${s.telefone}` : ""}
                      </small>
                      {s.recusado && (
                        <small className="curr-aviso-inline">Já marcado como "não é esta pessoa".</small>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => vincular(s)}
                      disabled={ocupado}
                    >
                      <IconLink size={14} /> Vincular
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )
        )}
        {erro && <div className="form-error curr-msg">{erro}</div>}
      </div>
    );
  }

  const r = disc.resposta;
  const marcadas = r.respostas ?? [];

  return (
    <div className="curr-respostas">
      <p className="disc-perfil curr-disc-perfil">
        Perfil predominante: <strong>{resultado.perfil}</strong> — {resultado.perfilNome}
        <small className="muted">
          {" "}
          · {resultado.respondidas} de {questoes.length} questões
        </small>
      </p>
      <small className="muted curr-nota">
        Teste "{disc.aplicacao.nome}"{disc.aplicacao.cargo ? ` (${disc.aplicacao.cargo})` : ""} ·{" "}
        {r.origem === "RH" ? "lançado pelo RH" : "respondido pelo link"} em {quando(r.respondido_em)}
      </small>

      <BarrasDisc
        placar={resultado.placar}
        respondidas={resultado.respondidas}
        totalQuestoes={questoes.length}
      />

      <div className="disc-leitura curr-disc-leitura">
        <h4>Leitura do resultado</h4>
        <ul>
          {leitura.caracteristicas.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        {leitura.atencao.length > 0 && (
          <>
            <h4>Pontos de atenção</h4>
            <ul className="disc-atencao">
              {leitura.atencao.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </>
        )}
      </div>

      {r.parecer ? (
        <div className="curr-observacao">
          <span>Parecer do RH{r.parecer_por ? ` — ${r.parecer_por}` : ""}</span>
          <p>{r.parecer}</p>
        </div>
      ) : (
        <p className="muted curr-nota">Ainda sem parecer do RH — ele é escrito na aba DISC.</p>
      )}
      {r.observacao && (
        <div className="curr-observacao">
          <span>Observação do DISC</span>
          <p>{r.observacao}</p>
        </div>
      )}

      {marcadas.length > 0 && (
        <button
          type="button"
          className="btn-secondary btn-sm curr-disc-expandir"
          onClick={() => setVerMarcadas((v) => !v)}
        >
          {verMarcadas ? "Ocultar o que ela marcou" : `Ver o que ela marcou (${marcadas.length})`}
        </button>
      )}
      {verMarcadas && (
        <ol className="disc-marcadas">
          {marcadas.map((m) => (
            <li key={m.q}>
              <span className="disc-marcada-n">{m.q}</span>
              <span className="disc-marcada-letra">{String(m.alternativa).toUpperCase()}</span>
              <span className="disc-marcada-txt">
                <em>{enunciadoDaQuestao(m.q, questoes)}</em>
                {textoAlternativa(m.q, m.alternativa, questoes)}
              </span>
            </li>
          ))}
        </ol>
      )}

      <div className="curr-rodape">
        <button type="button" className="btn-secondary btn-sm" onClick={imprimir}>
          <IconClipboard size={15} /> Imprimir laudo
        </button>
        <button
          type="button"
          className="btn-secondary btn-sm btn-danger-text"
          onClick={soltar}
          disabled={ocupado}
          title="O teste sai desta entrevista e continua na aba DISC"
        >
          Não é esta pessoa
        </button>
        <small className="muted curr-origem">
          {disc.vinculo === "manual"
            ? "Ligado pelo RH."
            : `Chegou sozinho: ${disc.como ? POR_QUE_DISC[disc.como] : "reconhecido pelo sistema"}.`}
        </small>
      </div>
      {erro && <div className="form-error curr-msg">{erro}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Painel                                                             *
 * ------------------------------------------------------------------ */

interface PainelProps {
  entrevista: Row;
  /** Avisa quem abriu quando o vínculo muda (a listagem pode querer recarregar). */
  onMudou?: (mensagem: string) => void;
}

export function CurriculoCandidatoPainel({ entrevista, onMudou }: PainelProps) {
  const entrevistaId = Number(entrevista.id);

  const [dossie, setDossie] = useState<DossieCandidato | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [aba, setAba] = useState<"curriculo" | "triagem" | "disc">("curriculo");

  const [sugestoes, setSugestoes] = useState<SugestaoCandidato[]>([]);
  const [vinculando, setVinculando] = useState<number | null>(null);
  const [enviando, setEnviando] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const carregar = useCallback(
    async (manterSelecao = false) => {
      setCarregando(true);
      setErro(null);
      try {
        const d = await api.candidatoEntrevista.obter(entrevistaId);
        setDossie(d);
        if (!manterSelecao) {
          // `principal` já vem como "origem:nome" — o nome sozinho abriria o
          // arquivo errado quando as duas pastas têm "curriculo.pdf".
          const inicial =
            d.arquivos.find((a) => chaveArquivo(a) === d.principal) ?? d.arquivos[0] ?? null;
          setSelecionado(inicial ? chaveArquivo(inicial) : null);
        }
        // Sem vínculo, já busca quem pode ser esta pessoa na triagem: assim o
        // RH resolve na hora, sem ter de abrir a outra aba para conferir.
        if (!d.vinculo) {
          const s = await api.candidatoEntrevista.sugestoes(entrevistaId).catch(() => null);
          setSugestoes(s?.sugestoes ?? []);
        } else {
          setSugestoes([]);
        }
      } catch (err) {
        setErro(err instanceof ApiError ? err.message : "Não consegui carregar o currículo.");
      } finally {
        setCarregando(false);
      }
    },
    [entrevistaId],
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  const arquivos = dossie?.arquivos ?? [];
  const atual = useMemo(
    () => arquivos.find((a) => chaveArquivo(a) === selecionado) ?? arquivos[0] ?? null,
    [arquivos, selecionado],
  );

  async function vincular(s: SugestaoCandidato) {
    // O servidor RECUSA (409) quem já está em outra entrevista — um candidato
    // tem uma entrevista só, garantido por índice único. Antes esta tela
    // perguntava se queria "desfazer o vínculo anterior", o que nunca
    // aconteceria: o clique só levava ao erro. Agora ela diz o que fazer.
    if (s.ja_vinculado) {
      setErro(
        `${s.candidato} já está ligado a outra entrevista. Abra aquela entrevista e desfaça o vínculo antes de ligar aqui.`,
      );
      return;
    }
    setVinculando(s.resposta_id);
    setErro(null);
    try {
      const r = await api.candidatoEntrevista.vincular(entrevistaId, s.resposta_id);
      setAviso(r.mensagem);
      await carregar();
      onMudou?.(r.mensagem);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não consegui ligar os dois cadastros.");
    } finally {
      setVinculando(null);
    }
  }

  async function desvincular() {
    if (!confirm("Desfazer o vínculo com a triagem? O currículo deixa de aparecer aqui.")) return;
    setErro(null);
    try {
      const r = await api.candidatoEntrevista.desvincular(entrevistaId);
      setAviso(r.mensagem);
      // Sem vínculo não há respostas da triagem: a aba fica desabilitada, e
      // continuar nela deixaria o painel mostrando um corpo que não existe.
      // (O DISC não depende da triagem — quem está nele, fica.)
      setAba((a) => (a === "triagem" ? "curriculo" : a));
      await carregar();
      onMudou?.(r.mensagem);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não consegui desfazer o vínculo.");
    }
  }

  /**
   * Anexa um currículo pelo próprio painel.
   *
   * Com vínculo, o arquivo vai para a pasta do CANDIDATO na triagem — é lá que
   * ele pertence, e assim aparece também no cartão da vaga. Sem vínculo, vai
   * para a pasta da entrevista (o único lugar que existe).
   */
  async function enviar(files: FileList | null) {
    if (!files || files.length === 0) return;
    setEnviando(true);
    setErro(null);
    try {
      const paraTriagem = Boolean(dossie?.vinculo);
      if (dossie?.vinculo) {
        await api.triagens.curriculo.upload(
          dossie.vinculo.triagem_id,
          dossie.vinculo.resposta_id,
          files,
        );
      } else {
        await api.documentosEntrevista.upload(entrevistaId, files);
      }
      // Abre o arquivo que ACABOU de ser enviado. Recarregar sem isto voltava
      // para o `principal` calculado no servidor, e quem anexou um currículo
      // novo continuava olhando o antigo, achando que o envio falhou.
      const enviado = Array.from(files)[files.length - 1]?.name ?? null;
      await carregar(true);
      if (enviado) setSelecionado(`${paraTriagem ? "triagem" : "entrevista"}:${enviado}`);
      setAviso("Currículo anexado.");
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Falha ao anexar o arquivo.");
    } finally {
      setEnviando(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  /** O RH ligou ou soltou o DISC à mão: recarrega sem trocar o currículo aberto. */
  async function discMudou(mensagem: string) {
    setAviso(mensagem);
    await carregar(true);
    onMudou?.(mensagem);
  }

  if (carregando && !dossie) {
    return <div className="curr-painel curr-carregando">Carregando o currículo…</div>;
  }

  const vinculo = dossie?.vinculo ?? null;
  const cand = dossie?.candidato ?? null;
  const respostas = cand?.respostas ?? [];

  return (
    <div className="curr-painel">
      {/* ---- Quem é a pessoa e de qual vaga ela veio ---- */}
      <div className="curr-topo">
        <div className="curr-quem">
          <strong>
            <IconUser size={15} /> {String(cand?.nome ?? entrevista.candidato ?? "candidato")}
          </strong>
          <small className="muted">
            {vinculo ? (
              <>
                Vaga: {vinculo.vaga}
                {vinculo.vaga_setor ? ` · ${vinculo.vaga_setor}` : ""}
                {notaTexto(cand?.nota) ? ` · triagem ${notaTexto(cand?.nota)}` : ""}
                {cand?.situacao ? ` · ${cand.situacao}` : ""}
              </>
            ) : (
              "Entrevista sem candidato ligado à Triagem de Currículo."
            )}
          </small>
        </div>
        <div className="curr-acoes-topo">
          <button
            type="button"
            className="icon-btn"
            title="Recarregar"
            onClick={() => carregar(true)}
          >
            <IconRefresh size={16} />
          </button>
          {vinculo && (
            <button
              type="button"
              className="icon-btn"
              title="Desfazer o vínculo com a triagem"
              onClick={desvincular}
            >
              <IconClose size={16} />
            </button>
          )}
        </div>
      </div>

      {erro && <div className="form-error curr-msg">{erro}</div>}
      {aviso && !erro && <div className="curr-ok curr-msg">{aviso}</div>}

      {/* ---- Sem vínculo: liga a entrevista a um candidato da triagem ---- */}
      {!vinculo && (
        <div className="curr-vincular">
          <p className="curr-nota">
            Ligue esta entrevista ao candidato da triagem para ver aqui o currículo que ele enviou e o
            que respondeu nas perguntas da vaga.
          </p>
          <ListaSugestoes sugestoes={sugestoes} vinculando={vinculando} onVincular={vincular} />
        </div>
      )}

      {/* ---- Currículo × respostas da triagem ---- */}
      <div className="curr-abas">
        <button
          type="button"
          className={aba === "curriculo" ? "curr-aba ativa" : "curr-aba"}
          onClick={() => setAba("curriculo")}
        >
          Currículo {arquivos.length > 0 && <span className="curr-cont">{arquivos.length}</span>}
        </button>
        <button
          type="button"
          className={aba === "triagem" ? "curr-aba ativa" : "curr-aba"}
          onClick={() => setAba("triagem")}
          disabled={!vinculo}
          title={vinculo ? "" : "Só existe com o candidato da triagem ligado"}
        >
          Respostas da triagem {respostas.length > 0 && <span className="curr-cont">{respostas.length}</span>}
        </button>
        <button
          type="button"
          className={aba === "disc" ? "curr-aba ativa" : "curr-aba"}
          onClick={() => setAba("disc")}
          title="Resultado do teste DISC — chega sozinho quando a pessoa responde"
        >
          DISC {dossie?.disc && <span className="curr-cont">{dossie.disc.resposta.perfil || "✓"}</span>}
        </button>
      </div>

      {aba === "disc" ? (
        <DiscDoCandidato entrevistaId={entrevistaId} disc={dossie?.disc ?? null} onMudou={discMudou} />
      ) : aba === "curriculo" ? (
        <>
          {arquivos.length > 1 && (
            <div className="curr-arquivos">
              {arquivos.map((a) => (
                <button
                  key={chaveArquivo(a)}
                  type="button"
                  className={atual && chaveArquivo(a) === chaveArquivo(atual) ? "curr-chip ativo" : "curr-chip"}
                  onClick={() => setSelecionado(chaveArquivo(a))}
                  title={`${a.nome} · ${tamanhoLegivel(a.tamanho)} · ${
                    a.origem === "triagem" ? "enviado na triagem" : "entregue na entrevista"
                  }`}
                >
                  <IconFile size={13} />
                  <span>{a.nome}</span>
                </button>
              ))}
            </div>
          )}

          <VisualizadorArquivo arquivo={atual} />

          <div className="curr-rodape">
            <input
              ref={fileRef}
              type="file"
              multiple
              hidden
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              onChange={(e) => enviar(e.target.files)}
            />
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => fileRef.current?.click()}
              disabled={enviando}
            >
              <IconUpload size={15} /> {enviando ? "Enviando…" : "Anexar currículo"}
            </button>
            {atual && (
              <>
                <a
                  className="btn-secondary btn-sm"
                  href={atual.url}
                  target="_blank"
                  rel="noreferrer"
                  title="Abrir em uma aba nova (tela cheia)"
                >
                  <IconDownload size={15} /> Abrir/baixar
                </a>
                <small className="muted curr-origem">
                  {atual.origem === "triagem" ? "enviado na triagem" : "entregue na entrevista"}
                  {atual.enviado_em ? ` · ${fmt(atual.enviado_em)}` : ""}
                  {atual.tamanho ? ` · ${tamanhoLegivel(atual.tamanho)}` : ""}
                </small>
              </>
            )}
          </div>
        </>
      ) : (
        <div className="curr-respostas">
          {/* O contato vem junto: é o que se usa para confirmar presença. */}
          {(cand?.email || cand?.telefone) && (
            <p className="curr-contato">
              {cand?.email && <span>{cand.email}</span>}
              {cand?.telefone && <span>{cand.telefone}</span>}
            </p>
          )}
          {cand?.observacao && (
            <div className="curr-observacao">
              <span>Observação do RH na triagem</span>
              <p>{cand.observacao}</p>
            </div>
          )}
          {respostas.length === 0 ? (
            <p className="muted curr-nota">
              O candidato não respondeu as perguntas da vaga (foi lançado pelo RH ou a vaga não tinha
              perguntas).
            </p>
          ) : (
            <ol className="curr-qa">
              {respostas.map((r, i) => (
                <li key={r.id || i}>
                  <p className="curr-pergunta">{r.texto}</p>
                  <p className={r.resposta ? "curr-resposta" : "curr-resposta vazia"}>
                    {r.resposta || "— sem resposta —"}
                  </p>
                </li>
              ))}
            </ol>
          )}
          {cand?.avaliado_por && (
            <small className="muted curr-nota">
              Avaliado por {cand.avaliado_por}
              {cand.avaliado_em ? ` em ${fmt(cand.avaliado_em)}` : ""}
            </small>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Modal (botão da linha na aba Entrevistas)                          *
 * ------------------------------------------------------------------ */

interface ModalProps {
  entrevista: Row;
  onClose: () => void;
  onMudou?: (mensagem: string) => void;
}

/** O mesmo painel, em modal próprio — para consultar sem abrir o roteiro. */
export default function CurriculoCandidatoModal({ entrevista, onClose, onMudou }: ModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card curr-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>
              <IconFile size={18} /> Currículo e DISC do candidato
            </h3>
            <p className="modal-sub">
              {String(entrevista.candidato ?? "candidato")}
              {entrevista.cargo ? ` · ${entrevista.cargo}` : ""}
              {entrevista.data ? ` · ${fmt(String(entrevista.data))}` : ""}
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>
        <div className="modal-body curr-modal-body">
          <CurriculoCandidatoPainel entrevista={entrevista} onMudou={onMudou} />
        </div>
        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
